// Audio queue with rate control (plan §8). The game's samples arrive in bursts (one batch per frame batch, at the
// game's own rate); the output pulls 128-frame quanta at the device rate. The queue is held near 85 ms (inside
// 70–100 ms) by nudging the resampling step by at most ±0.6 %: a game whose frame clock runs a little fast or slow
// against the device's audio clock (a 60 Hz loop feeding 59.73 Hz audio, Bluetooth, drifting clocks) would otherwise
// make the sound lag further and further behind or stutter. A PI controller on the smoothed queue level removes the
// steady-state offset a plain proportional nudge would leave.
//
// Shared by the AudioWorklet and the ScriptProcessor fallback; no DOM or worklet APIs here, so it is unit-tested.

export interface QueueOptions {
  targetMs?: number; // where the controller holds the queue
  maxMs?: number;    // above this (e.g. after the tab was hidden) the oldest samples are dropped back to target
  maxAdjust?: number; // largest change of the resampling step, as a fraction (0.006 = 0.6 %)
}

export interface QueueStats {
  queuedMs: number;
  smoothedMs: number;
  adjust: number;
  played: number;
  underruns: number;
  dropped: number;
  playing: boolean;
}

export class AudioQueue {
  readonly srcRate: number;
  readonly outRate: number;
  private readonly buf: Float32Array; // interleaved stereo ring
  private readonly cap: number; // frames
  private head = 0; // frame index of the oldest queued frame
  private len = 0; // queued frames
  private pos = 0; // fractional read position past head, in source frames
  private readonly base: number;
  private readonly target: number; // frames
  private readonly max: number;
  private readonly maxAdjust: number;
  private playing = false;
  private held = false;
  private smoothed = 0;
  private integ = 0;
  adjust = 0;
  played = 0;
  underruns = 0;
  dropped = 0;

  constructor(srcRate: number, outRate: number, o: QueueOptions = {}) {
    this.srcRate = srcRate;
    this.outRate = outRate;
    this.base = srcRate / outRate;
    this.target = Math.round(srcRate * (o.targetMs ?? 85) / 1000);
    this.max = Math.round(srcRate * (o.maxMs ?? 200) / 1000);
    this.maxAdjust = o.maxAdjust ?? 0.006;
    this.cap = Math.max(this.max * 2, 4096);
    this.buf = new Float32Array(this.cap * 2);
    this.smoothed = this.target;
  }

  /** Queued audio, in ms of source time (what is left to play, minus the part already consumed). */
  get queuedMs(): number { return ((this.len - this.pos) * 1000) / this.srcRate; }

  /** Interleaved stereo float samples (L, R, L, R, …). */
  push(s: Float32Array): void {
    let frames = s.length >> 1;
    let off = 0;
    if (frames > this.cap) { off = (frames - this.cap) * 2; this.dropped += frames - this.cap; frames = this.cap; }
    if (this.len + frames > this.cap) this.drop(this.len + frames - this.cap);
    let w = (this.head + this.len) % this.cap;
    for (let i = 0; i < frames; i++) {
      this.buf[w * 2] = s[off + i * 2];
      this.buf[w * 2 + 1] = s[off + i * 2 + 1];
      w = w + 1 === this.cap ? 0 : w + 1;
    }
    this.len += frames;
    if (this.len > this.max) this.drop(this.len - this.target);
  }

  /**
   * Hold while the game is paused: the output plays silence without consuming the queue (and without counting an
   * underrun), so the samples queued before the pause are still there when it resumes. Samples that arrive while
   * held (the last batch before the pause, the first ones after it while the device is still waking up) are kept.
   * Releasing the hold trims anything above the target, oldest first, so a pause never adds latency.
   */
  hold(on: boolean): void {
    if (on) { this.held = true; return; }
    if (!this.held) return;
    this.held = false;
    const excess = Math.floor(this.len - this.pos) - this.target;
    if (excess > 0) this.drop(excess);
  }

  get isHeld(): boolean { return this.held; }

  private drop(n: number) {
    n = Math.min(n, this.len);
    this.head = (this.head + n) % this.cap;
    this.len -= n;
    this.dropped += n;
    this.pos = 0;
  }

  /** Fill one output quantum (the two channel arrays have the same length). */
  fill(L: Float32Array, R: Float32Array): void {
    const n = L.length;
    if (this.held) { L.fill(0); R.fill(0); return; }
    // control: smooth the level over ~0.25 s, then a PI nudge of the step (positive = play faster)
    const dt = n / this.outRate;
    const a = 1 - Math.exp(-dt / 0.25);
    const level = this.len - this.pos - (n * this.base) / 2; // the level half-way through this quantum
    this.smoothed += (level - this.smoothed) * a;
    // full nudge at 10 ms off target; loop: kp 0.6/s, ki 0.18/s² (damping ~0.7, settles in a few seconds)
    const err = (this.smoothed - this.target) / (this.srcRate * 0.010);
    if (this.playing) this.integ = Math.max(-this.maxAdjust, Math.min(this.maxAdjust, this.integ + err * this.maxAdjust * dt * 0.3));
    this.adjust = Math.max(-this.maxAdjust, Math.min(this.maxAdjust, err * this.maxAdjust + this.integ));
    const step = this.base * (1 + this.adjust);

    if (!this.playing && this.len >= this.target) this.playing = true;
    for (let i = 0; i < n; i++) {
      if (!this.playing || this.len < 2 || this.pos + 1 >= this.len) {
        if (this.playing) { this.playing = false; this.underruns++; }
        L[i] = 0; R[i] = 0;
        continue;
      }
      const k = Math.floor(this.pos), f = this.pos - k;
      const i0 = (this.head + k) % this.cap, i1 = (i0 + 1) % this.cap;
      L[i] = this.buf[i0 * 2] + (this.buf[i1 * 2] - this.buf[i0 * 2]) * f;
      R[i] = this.buf[i0 * 2 + 1] + (this.buf[i1 * 2 + 1] - this.buf[i0 * 2 + 1]) * f;
      this.pos += step;
      const whole = Math.floor(this.pos);
      if (whole) {
        this.head = (this.head + whole) % this.cap;
        this.len -= whole;
        this.pos -= whole;
      }
      this.played++;
    }
  }

  stats(): QueueStats {
    return {
      queuedMs: this.queuedMs, smoothedMs: (this.smoothed * 1000) / this.srcRate, adjust: this.adjust,
      played: this.played, underruns: this.underruns, dropped: this.dropped, playing: this.playing,
    };
  }
}

/** int16 interleaved stereo -> float32. */
export function int16ToFloat(src: ArrayBuffer): Float32Array {
  const s = new Int16Array(src, 0, src.byteLength >> 1 & ~1);
  const out = new Float32Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s[i] / 32768;
  return out;
}
